import React from 'react';
import _ from 'lodash';
import propTypes from 'prop-types';
import moment from 'moment';
import momentBusiness from 'moment-business';
import i18n from './i18n';
import RadioGroup from '../radio-group/radio-group';
import Calendar from '../calendar/calendar';
import formatTimeSlot from '../../lib/formatters/format-time-slot';
import { submitForm } from '../../lib/form';
import Icon from '../icon/icon';
import { getAvailability } from '../../services/request-processor';
import getCalendarDaysForMonth from '../../lib/util/get-calendar-days-for-month';
import noClickAvailability from '../../lib/util/no-click-availability';
import processTimeSlots from '../../lib/util/availability';
import mergeI18n from '../../lib/util/merge-i18n';
import firstAvailabilityDate from '../../lib/util/first-availability-date';
import filterAllDayTimeSlots from '../../lib/util/filter-all-day-time-slots';
import { LOCATION_ROUTE, LOG_IN_ROUTE, REVIEW_ROUTE } from '../../constants/constants';
import Spinner from '../spinner/spinner';

class Schedule extends React.Component {
	constructor (props) {
		super(props);

		this.state = {
			calendarDays: [],
			selectedMonth: null,
			selectedDate: null,
			availabilityForLastMonth: false,
			availabilityForNextMonth: false,
			today: null,
			availability: [],
			timeSlots: [],
			selectedTime: null,
			isLoading: false,
			hasAcknowledgedRequirements: false
		};

		this.onBackSchedule = this.onBackSchedule.bind(this);
		this.onContinueSchedule = this.onContinueSchedule.bind(this);
		this.onSelectDate = this.onSelectDate.bind(this);
		this.onSelectTime = this.onSelectTime.bind(this);
		this.onClickNextMonth = this.onClickNextMonth.bind(this);
		this.onClickLastMonth = this.onClickLastMonth.bind(this);
		this.onToggleAcknowledgeRequirements = this.onToggleAcknowledgeRequirements.bind(this);
	}

	onBackSchedule () {
		this.props.setRoute(LOCATION_ROUTE);
	}

	onContinueSchedule () {
		this.props.setParentState({
			usingClick: this.state.usingClick,
			selectedTime: this.state.selectedTime,
			selectedDate: this.state.selectedDate
		});
		this.props.setRoute(REVIEW_ROUTE);
	}

	// Load all of the data
	componentDidMount () {
		if (_.isEmpty(this.props.inspectionRequest)) {
			this.props.setRoute(LOG_IN_ROUTE);
		} else {
			this.setState({ isLoading: true });
			this.props.setParentState({
				progressIndex: 3,
				canLogOut: false,
				canGoToHomePage: false
			});

			const params = {
				location: this.props.location,
				taskType: this.props.inspectionRequest.clickTaskType,
				requestId: this.props.inspectionRequest.confirmationNo,
				businessDays: import.meta.env.VITE_BUSINESS_DAYS,
				date: momentBusiness.addWeekDays(moment.utc(), 1).format('YYYY-MM-DDTHH:mm:ss')
			};
			const token = window.sessionStorage.getItem('token');

			getAvailability(params, token, (err, res) => {
				this.setState({ isLoading: false });

				const availability = _.get(res, 'body.results');
				const today = moment.utc().startOf('day').toISOString();

				if (err || !availability || !availability.length) {
					const timeSlots = processTimeSlots(noClickAvailability(momentBusiness.addWeekDays(moment.utc(), 3).toISOString(), 30));
					const selectedMonth = moment.utc(new Date(firstAvailabilityDate(timeSlots))).startOf('month').toString();
					const calendarDays = getCalendarDaysForMonth(timeSlots, selectedMonth, null, today);

					this.setState({
						availability: timeSlots,
						today: today,
						usingClick: false,
						selectedDate: null,
						selectedTime: null,
						timeSlots: [],
						selectedMonth,
						...calendarDays
					});
				} else {
					const timeSlots = processTimeSlots(filterAllDayTimeSlots(availability));
					const selectedMonth = moment.utc(new Date(firstAvailabilityDate(timeSlots))).startOf('month').toString();
					const calendarDays = getCalendarDaysForMonth(timeSlots, selectedMonth, null, today);

					this.setState({
						availability: timeSlots,
						today: today,
						usingClick: true,
						selectedDate: null,
						selectedTime: null,
						timeSlots: [],
						selectedMonth,
						...calendarDays
					});
				}
			});
		}
	}

	onSelectDate (dateParam) {
		this.setState((previousState) => {
			const calendarDays = previousState.calendarDays.map(day => ({ ...day, isSelected: day.date === dateParam }));
			const timeSlots = _.get(previousState.calendarDays.find(({ date }) => date === dateParam), 'timeSlots');

			return {
				selectedDate: dateParam,
				selectedTime: null,
				calendarDays: calendarDays,
				timeSlots: timeSlots
			};
		});
	}

	onSelectTime (ev) {
		ev.persist();
		this.setState((previousState) => {
			const time = previousState.timeSlots.find(slot => formatTimeSlot(slot).display === ev.target.value);
			return { selectedTime: time };
		});
	}

	onClickNextMonth () {
		this.setState((previousState) => {
			const selectedMonth = moment.utc(new Date(previousState.selectedMonth)).add(1, 'month').startOf('month').toString();
			return {
				selectedMonth: selectedMonth,
				...getCalendarDaysForMonth(previousState.availability, selectedMonth, previousState.selectedDate, previousState.today)
			};
		});
	}

	onToggleAcknowledgeRequirements (ev) {
		this.setState({ hasAcknowledgedRequirements: ev.target.checked });
	}

	onClickLastMonth () {
		this.setState((previousState) => {
			const selectedMonth = moment.utc(new Date(previousState.selectedMonth))
				.subtract(1, 'month')
				.startOf('month')
				.toString();
			const calendarDays = getCalendarDaysForMonth(this.state.availability, selectedMonth, this.state.selectedDate, this.state.today);

			return {
				selectedMonth: selectedMonth,
				...calendarDays
			};
		});
	}

	render () {
		// Dispite changing the route in componentDidMount React will attempt to render this
		// at least once, so we're doing this to avoid a crash
		if (_.isEmpty(this.props.inspectionRequest)) {
			return (<div />);
		}

		if (this.state.isLoading) {
			return (<Spinner />);
		}

		const __ = mergeI18n(this.props.language, i18n);

		const formSubmit = submitForm(this.state.selectedDate && this.state.selectedTime && this.state.hasAcknowledgedRequirements, this.onContinueSchedule);

		return (
			<div id='lss-content' className='container'>
				<div className='row'>
					<div className='col-xs-12'>
						<div id='schedule'>
							<form onSubmit={formSubmit}>
								<h1>{__.title(this.state.usingClick)}</h1>
								<h2>{this.state.usingClick ? __.instructionsClick : __.instructionsNoClick}</h2>
								<div className='row'>
									<div className='col-xs-12 col-sm-8 col-md-7 col-lg-6 calendar'>
										<Calendar
											language={this.props.language}
											selectedMonth={this.state.selectedMonth}
											calendarDays={this.state.calendarDays}
											availabilityForLastMonth={this.state.availabilityForLastMonth}
											availabilityForNextMonth={this.state.availabilityForNextMonth}
											onSelectDate={this.onSelectDate}
											onClickLastMonth={this.onClickLastMonth}
											onClickNextMonth={this.onClickNextMonth}
										/>
									</div>
									<div className='col-xs-12 col-sm-4 col-md-5 col-lg-6 choose-time-column'>
										<h3>{this.state.selectedDate ? __.availableTimeSlot(this.state.usingClick, this.state.selectedDate, this.state.timeSlots) : null}</h3>
										<RadioGroup
											name='choose-time-slot'
											value={(this.state.selectedTime && formatTimeSlot(this.state.selectedTime).display) || ''}
											onChange={this.onSelectTime}
											onBlur={this.onSelectTime}
											options={(this.state.timeSlots || []).map(formatTimeSlot)}
											checked={(this.state.selectedTime && formatTimeSlot(this.state.selectedTime).display)}
											verticalLayout={true}
										/>
									</div>
								</div>
								<div className='row vehicle-preparation'>
									<div className='col-xs-12'>
										<div className='checkbox'>
											<label htmlFor='hasAcknowledgedRequirements'>
												<input
													type='checkbox'
													id='hasAcknowledgedRequirements'
													name='hasAcknowledgedRequirements'
													checked={this.state.hasAcknowledgedRequirements}
													onChange={this.onToggleAcknowledgeRequirements}
												/>
												{__.acknowledgeRequirements}
											</label>
										</div>
										<p>
											<strong>{__.vehiclePreparationLabel}</strong> {__.vehiclePreparation}
										</p>
									</div>
								</div>
								<div className='flex-button-spacer' />
								<div className='form-buttons-wrapper'>
									<div className='form-buttons'>
										<button id='back-button' className='btn btn-default back-button' type='button' onClick={this.onBackSchedule}><Icon name='MdChevronLeft' /> {__.backButton}</button>
										<button
											className='btn btn-primary continue-button'
											type='submit'
											disabled={!this.state.selectedDate || !this.state.selectedTime || !this.state.hasAcknowledgedRequirements}
										>
											{__.continueButton} <Icon name='MdChevronRight' />
										</button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

Schedule.propTypes = {
	inspectionRequest: propTypes.shape({
		confirmationNo: propTypes.number,
		clickTaskType: propTypes.string,
		lesseeFirstName: propTypes.string,
		lesseeLastName: propTypes.string,
		lesseeEmail: propTypes.string,
		vehicles: propTypes.arrayOf(propTypes.shape({
			vin: propTypes.string.isRequired,
			make: propTypes.string,
			model: propTypes.string,
			year: propTypes.string
		}))
	}),
	language: propTypes.string.isRequired,
	setParentState: propTypes.func.isRequired,
	setRoute: propTypes.func.isRequired
};
export default Schedule;
